import {
  BasicStep,
  PitbullProvinces,
  PreconfirmationStep,
  Option,
  MidgardQuestions,
} from "../../types";
import {
  asCuit,
  getFormattedPreconfirmationFields,
  getUnificationParams,
  isAllariaMasOnboarding,
  isUnifiedOnboarding,
} from "../../utils";

// STEPS
import employmentProfileStep from "./employmentProfileStep";
import investmentProfileStep from "./investmentProfileStep";
import personalInformationStep from "./personalInformationStep";

const preconfirmationSteps = (
  countriesOptions: Option[],
  citizenshipOptions: Option[],
  activitiesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
  questions: MidgardQuestions
) => [
  ...(isUnifiedOnboarding() && isAllariaMasOnboarding()
    ? [unificationPreconfirmationStep]
    : []),
  personalInfoPreconfirmationStep(
    countriesOptions,
    citizenshipOptions,
    getProvincesFilteredByCountry,
    provinces
  ),
  employmentProfilePreconfirmationStep(
    activitiesOptions,
    countriesOptions,
    getProvincesFilteredByCountry,
    provinces
  ),
  investmentProfilePreconfirmationStep(questions),
];

const unificationPreconfirmationStep: PreconfirmationStep = {
  name: "Unificación de cuentas",
  columns: 4,
  fields: [
    {
      name: "company",
      label: "Empresa",
      type: "text",
      valueGetter: () => "Allaria",
      isConditional: false,
    },
    {
      name: "accountId",
      label: "Número de cuenta",
      type: "cuit",
      valueGetter: () => `#${getUnificationParams().accountId}`,
      isConditional: false,
    },
    {
      name: "cuit",
      label: "CUIT / CUIL",
      type: "cuit",
      valueGetter: () => asCuit(getUnificationParams().cuit),
      isConditional: false,
    },
    {
      name: "accountName",
      label: "Nombre",
      type: "cuit",
      valueGetter: () => getUnificationParams().accountName,
      isConditional: false,
    },
  ],
};

const personalInfoPreconfirmationStep = (
  countriesOptions: Option[],
  citizenshipOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces
): PreconfirmationStep => {
  const stepFourFields = (
    personalInformationStep(
      countriesOptions,
      citizenshipOptions,
      getProvincesFilteredByCountry,
      provinces
    ).subSteps ?? []
  ).flatMap((subStep: BasicStep) => subStep.fields ?? []);

  const stepFourFieldsFormatted =
    getFormattedPreconfirmationFields(stepFourFields);

  return {
    name: "Datos personales",
    columns: 4,
    fields: [
      {
        name: "cuit",
        label: "CUIT / CUIL",
        type: "cuit",
        formatInPreConfirmation: (value) => asCuit(value),
        isConditional: false,
      },
      ...stepFourFieldsFormatted.slice(0, 9),
      ...stepFourFieldsFormatted.slice(11, 18),
      ...stepFourFieldsFormatted.slice(9, 11),
    ],
    editStep: {
      step: 4,
      substep: 1,
    },
  };
};

const employmentProfilePreconfirmationStep = (
  activitiesOptions: Option[],
  countriesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces
): PreconfirmationStep => {
  const stepFiveFields =
    employmentProfileStep(
      activitiesOptions,
      countriesOptions,
      getProvincesFilteredByCountry,
      provinces
    ).fields ?? [];

  const stepFiveFieldsFormatted =
    getFormattedPreconfirmationFields(stepFiveFields);

  return {
    name: "Situación laboral",
    columns: 4,
    fields: [
      ...stepFiveFieldsFormatted.slice(0, 3),
      ...stepFiveFieldsFormatted.slice(4, 8),
    ],
    editStep: {
      step: 5,
    },
  };
};

const investmentProfilePreconfirmationStep = (
  questions: MidgardQuestions
): PreconfirmationStep => {
  const stepSixFields = (
    investmentProfileStep(questions).subSteps ?? []
  ).flatMap((subStep: BasicStep) => subStep.fields ?? []);

  const stepSixFieldsFormatted =
    getFormattedPreconfirmationFields(stepSixFields);

  return {
    name: "Perfil inversor",
    columns: 2,
    fields: [
      ...stepSixFieldsFormatted.slice(0, 5),
      stepSixFieldsFormatted[7],
      stepSixFieldsFormatted[5],
      stepSixFieldsFormatted[6],
    ],
    editStep: {
      step: 6,
      substep: 1,
    },
  };
};

export default preconfirmationSteps;
